import { useState, useCallback } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import {
    getAllLeaves,
    createLeave,
    updateLeave,
    deleteLeave,
    getLeaveById,
    LeaveSearchParams,
    getMyLeaves,
} from "../../../services/leaveService";
import {
    Leave,
    LeaveCreate,
    LeaveUpdate,
} from "../../../types/leaveTypes";



export const useLeave = () => {
    const { t } = useTranslation();

    const [leaves, setLeaves] = useState<Leave[]>([]);
    const [pagination, setPagination] = useState<any>({
        page: 1,
        limit: 10,
        total: 0,
    });
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const statusLeaveOptions = [
        { label: t("leave.status.pending"), value: "pending" },
        { label: t("leave.status.approved"), value: "approved" },
        { label: t("leave.status.rejected"), value: "rejected" },
    ];

    const periodLeaveOptions = [
        { label: t("leave.period.full"), value: "full" },
        { label: t("leave.period.morning"), value: "morning" },
        { label: t("leave.period.afternoon"), value: "afternoon" },
    ];

    const fetchLeaves =
        async (filters: LeaveSearchParams) => {
            setLoading(true);
            try {
                const response = await getAllLeaves(filters);
                setLeaves(response.data);
                setPagination(response.pagination);
                setError(null);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        }

    const fetchMyLeaves = async (filters: LeaveSearchParams) => {
        setLoading(true);
        try {
            const response = await getMyLeaves(filters);
            setLeaves(response.data);
            setPagination(response.pagination);
            setError(null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }

    const handleCreate = async (payload: LeaveCreate) => {
        try {
            await createLeave(payload);
            message.success(t("leave.success_create"));
            await fetchMyLeaves({ page: 1, limit: 10 });
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const handleUpdate = async (leaveId: string, payload: LeaveUpdate) => {
        try {
            await updateLeave(leaveId, payload);
            message.success(t("leave.success_update"));
            await fetchMyLeaves({ page: 1, limit: 10 });
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };


    const handleUpdateStatus = async (leaveId: string, payload: LeaveUpdate) => {
        try {
            await updateLeave(leaveId, payload);
            message.success(t("leave.success_update"));
            await fetchLeaves({ page: 1, limit: 10 });
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const handleDelete = async (leaveId: string) => {
        try {
            await deleteLeave(leaveId);
            message.success(t("leave.success_delete"));
            await fetchMyLeaves({ page: 1, limit: 10 });
            return true;
        } catch (err) {
            console.error(err);
            return false;
        }
    };

    const handleGetLeaveById = async (leaveId: string) => {
        try {
            const response = await getLeaveById(leaveId);
            return response?.data as Leave;
        } catch (err) {
            console.error(err);
            return null;
        }
    };

    return {
        leaves,
        infoPagination: {
            total: pagination?.total,
            page: pagination?.page,
            limit: pagination?.limit,
        },
        loading,
        error,
        selectedLeave,
        setSelectedLeave,
        isModalOpen,
        setIsModalOpen,
        fetchLeaves,
        handleCreate,
        handleUpdate,
        handleDelete,
        handleGetLeaveById,
        statusLeaveOptions,
        periodLeaveOptions,
        fetchMyLeaves,
        handleUpdateStatus
    };
};

export default useLeave;
